/**
 * ReportCharts Component - Pie and Bar charts for report visualization
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { 
  PieChart, 
  Pie, 
  Cell, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { colors, CHART_COLORS } from './constants';

export const StatusPieChart = ({ summary }) => {
  const data = [
    { name: 'Gültig', value: summary?.completed || 0 },
    { name: 'Abgelaufen', value: summary?.expired || 0 },
    { name: 'Offen', value: summary?.not_completed || 0 }
  ].filter(d => d.value > 0);

  return (
    <Card className="bg-white" style={{ borderColor: colors.border }}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base" style={{ color: colors.dark }}>Verteilung</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={250}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={80}
              paddingAngle={2}
              dataKey="value"
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              labelLine={false}
            >
              <Cell fill={CHART_COLORS.completed} />
              <Cell fill={CHART_COLORS.expired} />
              <Cell fill={CHART_COLORS.notCompleted} />
            </Pie>
            <Tooltip formatter={(value) => [value, 'Anzahl']} />
          </PieChart>
        </ResponsiveContainer>
        <div className="flex justify-center gap-4 mt-2">
          <div className="flex items-center gap-1 text-xs">
            <div className="w-3 h-3 rounded" style={{ background: CHART_COLORS.completed }} />
            <span>Gültig</span>
          </div>
          <div className="flex items-center gap-1 text-xs">
            <div className="w-3 h-3 rounded" style={{ background: CHART_COLORS.expired }} />
            <span>Abgelaufen</span>
          </div>
          <div className="flex items-center gap-1 text-xs">
            <div className="w-3 h-3 rounded" style={{ background: CHART_COLORS.notCompleted }} />
            <span>Offen</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const StatusBarChart = ({ summary }) => {
  const data = [
    { name: 'Gültig', value: summary?.completed || 0, fill: CHART_COLORS.completed },
    { name: 'Abgelaufen', value: summary?.expired || 0, fill: CHART_COLORS.expired },
    { name: 'Offen', value: summary?.not_completed || 0, fill: CHART_COLORS.notCompleted }
  ];

  return (
    <Card className="bg-white" style={{ borderColor: colors.border }}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base" style={{ color: colors.dark }}>Übersicht</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 60, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
            <XAxis type="number" />
            <YAxis type="category" dataKey="name" />
            <Tooltip formatter={(value) => [value, 'Anzahl']} />
            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// Ablauf-specific Pie Chart
export const AblaufPieChart = ({ summary, activeFilter }) => {
  // If a specific filter is active, show only that category
  if (activeFilter) {
    const isExpired = activeFilter.startsWith('A');
    const levels = isExpired ? summary?.expired_levels : summary?.expiring_soon_levels;
    const data = [
      { name: activeFilter, value: levels?.[activeFilter] || 0 }
    ].filter(d => d.value > 0);

    const fillColor = isExpired ? '#ef4444' : '#f59e0b';
    
    return (
      <Card className="bg-white" style={{ borderColor: colors.border }}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base" style={{ color: colors.dark }}>
            Verteilung - {activeFilter}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[250px]">
            <div className="text-center">
              <div className="text-5xl font-bold" style={{ color: fillColor }}>
                {levels?.[activeFilter] || 0}
              </div>
              <div className="text-sm mt-2" style={{ color: colors.gray }}>
                {isExpired ? 'Abgelaufene Einträge' : 'Bald ablaufende Einträge'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Default: Show distribution of expiring soon vs expired vs valid vs open
  const data = [
    { name: 'Gültig', value: summary?.valid || 0 },
    { name: 'Bald ablaufend', value: summary?.expiring_soon || 0 },
    { name: 'Offen', value: summary?.open || 0 },
    { name: 'Abgelaufen', value: summary?.expired || 0 },
    { name: 'Ohne Datum', value: summary?.no_expiry_date || 0 }
  ].filter(d => d.value > 0);

  const ABLAUF_COLORS = ['#22c55e', '#f59e0b', '#f97316', '#ef4444', '#94a3b8'];

  return (
    <Card className="bg-white" style={{ borderColor: colors.border }}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base" style={{ color: colors.dark }}>Verteilung</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={250}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={80}
              paddingAngle={2}
              dataKey="value"
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              labelLine={false}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={ABLAUF_COLORS[index % ABLAUF_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => [value, 'Anzahl']} />
          </PieChart>
        </ResponsiveContainer>
        <div className="flex justify-center flex-wrap gap-3 mt-2">
          <div className="flex items-center gap-1 text-xs">
            <div className="w-3 h-3 rounded" style={{ background: '#22c55e' }} />
            <span>Gültig</span>
          </div>
          <div className="flex items-center gap-1 text-xs">
            <div className="w-3 h-3 rounded" style={{ background: '#f59e0b' }} />
            <span>Bald ablaufend</span>
          </div>
          <div className="flex items-center gap-1 text-xs">
            <div className="w-3 h-3 rounded" style={{ background: '#f97316' }} />
            <span>Offen</span>
          </div>
          <div className="flex items-center gap-1 text-xs">
            <div className="w-3 h-3 rounded" style={{ background: '#ef4444' }} />
            <span>Abgelaufen</span>
          </div>
          <div className="flex items-center gap-1 text-xs">
            <div className="w-3 h-3 rounded" style={{ background: '#94a3b8' }} />
            <span>Ohne Datum</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Ablauf-specific Bar Chart
export const AblaufBarChart = ({ summary, activeFilter }) => {
  // If a specific filter is active, show breakdown of that level
  if (activeFilter) {
    const isExpired = activeFilter.startsWith('A');
    const levels = isExpired ? summary?.expired_levels : summary?.expiring_soon_levels;
    
    const data = isExpired 
      ? [
          { name: 'A1 (bis 2 Wo.)', value: levels?.A1 || 0, fill: '#fca5a5' },
          { name: 'A2 (2-4 Wo.)', value: levels?.A2 || 0, fill: '#f87171' },
          { name: 'A3 (> 4 Wo.)', value: levels?.A3 || 0, fill: '#ef4444' }
        ]
      : [
          { name: 'L1 (< 4 Wo.)', value: levels?.L1 || 0, fill: '#fcd34d' },
          { name: 'L2 (4-6 Wo.)', value: levels?.L2 || 0, fill: '#fbbf24' },
          { name: 'L3 (6-8 Wo.)', value: levels?.L3 || 0, fill: '#f59e0b' }
        ];

    return (
      <Card className="bg-white" style={{ borderColor: colors.border }}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base" style={{ color: colors.dark }}>
            Übersicht - {isExpired ? 'Abgelaufen' : 'Läuft bald ab'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" />
              <Tooltip formatter={(value) => [value, 'Anzahl']} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    );
  }

  // Default: Show all categories
  const data = [
    { name: 'Gültig', value: summary?.valid || 0, fill: '#22c55e' },
    { name: 'L1', value: summary?.expiring_soon_levels?.L1 || 0, fill: '#fcd34d' },
    { name: 'L2', value: summary?.expiring_soon_levels?.L2 || 0, fill: '#fbbf24' },
    { name: 'L3', value: summary?.expiring_soon_levels?.L3 || 0, fill: '#f59e0b' },
    { name: 'Offen', value: summary?.open || 0, fill: '#f97316' },
    { name: 'A1', value: summary?.expired_levels?.A1 || 0, fill: '#fca5a5' },
    { name: 'A2', value: summary?.expired_levels?.A2 || 0, fill: '#f87171' },
    { name: 'A3', value: summary?.expired_levels?.A3 || 0, fill: '#ef4444' },
    { name: 'Ohne Datum', value: summary?.no_expiry_date || 0, fill: '#94a3b8' }
  ];

  return (
    <Card className="bg-white" style={{ borderColor: colors.border }}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base" style={{ color: colors.dark }}>Übersicht nach Stufen</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
            <XAxis type="number" />
            <YAxis type="category" dataKey="name" />
            <Tooltip formatter={(value) => [value, 'Anzahl']} />
            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export const CourseBarChart = ({ courses }) => {
  if (!courses || courses.length === 0) return null;

  const data = courses.slice(0, 15).map(c => ({
    name: c.course.length > 30 ? c.course.substring(0, 30) + '...' : c.course,
    Gültig: c.completed,
    Abgelaufen: c.expired || 0,
    Offen: c.not_completed,
    rate: c.completion_rate
  }));

  // Reduced bar height: 28px per course instead of 50px
  const chartHeight = Math.max(250, data.length * 28);

  return (
    <Card className="bg-white md:col-span-2" style={{ borderColor: colors.border }}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base" style={{ color: colors.dark }}>Kurse - Abschlussrate</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 150, bottom: 5 }}
            barSize={16}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
            <XAxis type="number" />
            <YAxis 
              type="category" 
              dataKey="name" 
              width={140} 
              tick={{ fontSize: 11, fontWeight: 'bold', fill: colors.dark }} 
            />
            <Tooltip />
            <Legend />
            <Bar dataKey="Gültig" stackId="a" fill={CHART_COLORS.completed} />
            <Bar dataKey="Abgelaufen" stackId="a" fill={CHART_COLORS.expired} />
            <Bar dataKey="Offen" stackId="a" fill={CHART_COLORS.notCompleted} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};
